import { useForm } from '@mantine/form';
import { FormEvent, useEffect, useLayoutEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import { useQuestion, WIDGET_FOOTER_DEFAULT_TEXT } from '@mygpt/app';
import { useSessionID } from '@mygpt/app/utils';
import { SessionListItem } from '@/types/interface';

import ChatView, { I_configs } from './ChatView';

export default () => {
  const { id } = useParams();
  const messageId = uuid();

  const form = useForm({
    initialValues: {
      value: '',
    },
  });

  const [title, setTitle] = useState<string>('');
  const [configs, setConfigs] = useState<I_configs>();
  const [historySessionList, setHistorySessionList] = useState<SessionListItem>({ messages: [] });

  const { refreshId, sessionId } = useSessionID(id);
  const { clearSessionList, fetchQuestion, loading, sessionList, stopAnswer, updateSessionList } = useQuestion({
    aiId: id || '',
    sessionId,
    messageId,
  });

  const resetPageStyle = () => {
    document.querySelector('html')?.setAttribute('style', 'height:100%');
    document.querySelector('body')?.classList.add('h-full');
    document.querySelector('#root')?.classList.add('h-full');
  };

  const submit = async (values: { value: string }, event?: FormEvent<HTMLFormElement>) => {
    event && event.preventDefault();
    if (loading) return;
    const { value } = values;
    form.reset();
    fetchQuestion(value);
  };

  const onClose = function () {
    window.parent.postMessage({ closeIframe: true }, '*');
  };

  const clearSession = () => {
    setHistorySessionList({ messages: [] });
    clearSessionList();
    refreshId();
  };

  const saveChat = () => {
    // 有一问一答的时候才记录
    if (messages.length && messages.at(-1)?.isAnswer) {
      const chatList = [...historySessionList.messages, ...messages.slice(-2)];
      setHistorySessionList({ messages: chatList });
      updateSessionList({ messages: [] });
    }
  };

  const listen = (messageEvent: MessageEvent) => {
    if (!messageEvent.data.configs) return;
    const { chatbot, chatbotAvatar } = messageEvent.data.configs;
    if (!chatbot || !chatbotAvatar) return;

    const signatureMessages =
      chatbot.signatureMessages &&
      chatbot.signatureMessages.checked &&
      ['all', 'widget'].includes(chatbot.signatureMessages.displayType)
        ? {
            value: chatbot.signatureMessages.value,
            list: chatbot.signatureMessages.list,
            checked: chatbot.signatureMessages.checked,
            displayType: chatbot.signatureMessages.display,
          }
        : undefined;
    const suggestedMessages = chatbot.suggestedMessages.checked ? chatbot.suggestedMessages.value.split(/[\n]/) : [];
    const inputPlaceholder = chatbot.inputPlaceholder.checked ? chatbot.inputPlaceholder.value : '';
    const chatbotWidgetColor = chatbot.widgetColor;
    const widgetColor = {
      chatColor: chatbotWidgetColor.chatColor.checked ? chatbotWidgetColor.chatColor.value : '',
      sendButtonColor: chatbotWidgetColor.sendButtonColor.checked ? chatbotWidgetColor.sendButtonColor.value : '',
      userMessageColor: chatbotWidgetColor.userMessageColor.checked ? chatbotWidgetColor.userMessageColor.value : '',
      botMessageColor: chatbotWidgetColor.botMessageColor.checked ? chatbotWidgetColor.botMessageColor.value : '',
    };
    const { checked: footerBrandChecked, value: footerBrandValue } = chatbot.footerBrand;
    const footerBrand = footerBrandChecked && footerBrandValue !== WIDGET_FOOTER_DEFAULT_TEXT ? footerBrandValue : '';

    setConfigs({
      signatureMessages,
      suggestedMessages,
      inputPlaceholder,
      footerBrand,
      widgetColor,
      chatbotAvatar,
      answerSource: chatbot.answerSource,
    });

    setTitle(chatbot.chatHeaderDisplay.checked ? chatbot.chatHeaderDisplay.value : messageEvent.data.title);
    if (chatbot.welcomeMessage.checked) {
      setHistorySessionList({
        messages: chatbot.welcomeMessage.value.split(/[\n]/).map((item: string) => {
          return {
            isAnswer: true,
            text: item,
          };
        }),
      });
    }
  };

  useLayoutEffect(() => {
    resetPageStyle();
  }, []);

  // post message 通信 from plugin.js
  useLayoutEffect(() => {
    window.addEventListener('message', listen);
    window.parent.postMessage({ initEnd: true }, '*');

    return () => {
      window.removeEventListener('message', listen);
    };
  }, []);

  // 聊天记录
  const { messages } = sessionList;
  useEffect(() => {
    if (!messages.length) return;
    saveChat();
  }, [messages]);

  return (
    <ChatView
      title={title}
      onClose={onClose}
      onRefresh={clearSession}
      loading={loading}
      sessionList={sessionList}
      historySessionList={historySessionList}
      submit={submit}
      form={form}
      configs={configs}
      sessionInfo={{
        aiId: id || '',
        sessionId,
      }}
      setHistorySessionList={setHistorySessionList}
      updateSessionList={updateSessionList}
      fetchQuestion={fetchQuestion}
      stopAnswer={stopAnswer}
    />
  );
};
